/*
 * Karigur — lightweight, privacy-safe conversion instrumentation.
 *
 * Pushes named events to window.dataLayer so GA4 / Google Tag Manager / Meta can
 * consume them when configured admin-side. It is a harmless no-op if no tag manager
 * is present. Zero third-party code, deferred, ~1KB.
 *
 * Wiring:
 *   - Add data-kb-event="<event_name>" to any link/button to track a click.
 *     (Optional: data-kb-pathway="custom" to pass extra context, e.g. from the path finder.)
 *   - Add data-kb-lead-success (+ optional data-kb-lead-type="bridal_inquiry") to the
 *     element that only renders on a successful lead-form submission.
 *
 * Events emitted (consumed by GA4 key events when mapped in GTM):
 *   book_consultation_click, ask_about_look_click, sticky_book_click, sticky_secondary_click,
 *   whatsapp_click, path_finder_cta, view_item (bridal PDP), generate_lead,
 *   add_to_cart, begin_checkout, …and any custom data-kb-event value.
 */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  function push(name, detail) {
    try {
      window.dataLayer.push(Object.assign({ event: name }, detail || {}));
    } catch (e) {}
  }

  // Expose a global tracker so other theme scripts (estimator, saved looks) can
  // emit the same named dataLayer events without re-implementing the guard.
  window.kbTrack = push;

  // Delegated click tracking for any element carrying [data-kb-event].
  document.addEventListener(
    'click',
    function (e) {
      var el = e.target && e.target.closest ? e.target.closest('[data-kb-event]') : null;
      if (!el) return;
      var name = el.getAttribute('data-kb-event');
      if (!name) return;
      push(name, {
        kb_label: (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80),
        kb_href: el.getAttribute('href') || '',
        kb_pathway: el.getAttribute('data-kb-pathway') || ''
      });
    },
    { passive: true }
  );

  // Lead-form success: fire generate_lead when a success marker is present on load.
  function detectLeadSuccess() {
    var ok = document.querySelector('[data-kb-lead-success]');
    if (!ok) return;
    push('generate_lead', {
      kb_lead_type: ok.getAttribute('data-kb-lead-type') || 'bridal_inquiry'
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', detectLeadSuccess);
  } else {
    detectLeadSuccess();
  }

  // Ecommerce funnel events — pushed to dataLayer (no-op until GA4/GTM is configured admin-side).
  //   add_to_cart    — any /cart/add form submit
  //   begin_checkout — checkout buttons / links to /checkout
  // (view_item + purchase need per-product data + a Customer-Events Web Pixel — see docs/73 Sprint 7.)
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && f.action && /\/cart\/add/.test(f.action)) push('add_to_cart', {});
  }, { passive: true });
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest
      ? e.target.closest('[name="checkout"], a[href*="/checkout"], [data-kb-event="begin_checkout"]')
      : null;
    if (el) push('begin_checkout', {});
  }, { passive: true });

  // Saved Looks: translate the internal change event into a lead-funnel signal.
  // Fire saved_look_add only when the saved count grows (ignore removals).
  var kbSavedCount = -1;
  document.addEventListener('kb:saved:change', function (e) {
    var n = (e && e.detail && typeof e.detail.count === 'number') ? e.detail.count : null;
    if (n === null) return;
    if (kbSavedCount !== -1 && n > kbSavedCount) push('saved_look_add', { kb_saved_count: n });
    kbSavedCount = n;
  }, { passive: true });

  // Newsletter signup: the footer "Letters from the house" form.
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && f.classList && f.classList.contains('email-signup__form')) push('newsletter_signup', {});
  }, { passive: true });

  // Scroll depth on content-heavy pages (fires each threshold once).
  var kbDepths = [25, 50, 75, 100];
  var kbHit = {};
  function kbScroll() {
    var doc = document.documentElement;
    var scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    var pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
    for (var i = 0; i < kbDepths.length; i++) {
      var d = kbDepths[i];
      if (pct >= d && !kbHit[d]) { kbHit[d] = true; push('scroll_depth', { kb_percent: d }); }
    }
    if (kbHit[100]) window.removeEventListener('scroll', kbScrollThrottled);
  }
  var kbTicking = false;
  function kbScrollThrottled() {
    if (kbTicking) return;
    kbTicking = true;
    window.requestAnimationFrame(function () { kbScroll(); kbTicking = false; });
  }
  window.addEventListener('scroll', kbScrollThrottled, { passive: true });

  // Karigur: SEAMLESS contact-form submit. A native {% form 'contact' %} does a full
  // page POST then reloads to ?contact_posted=true, tearing down the page and snapping
  // to the top — the disorienting "glitch". Intercept it: POST in the background and
  // reveal the success message in place, no reload, no scroll jump. Progressive
  // enhancement: with no fetch/FormData, or on network failure, the native submit runs.
  function kbShowSent(f) {
    var msg = f.getAttribute('data-kb-success') || 'Thank you. Your request is with our bridal team.';
    var note = document.createElement('p');
    note.className = 'kb-lead-sent';
    note.setAttribute('role', 'status');
    note.setAttribute('data-kb-lead-success', '');
    note.setAttribute('data-kb-lead-type', f.getAttribute('data-kb-form-type') || 'lead');
    note.textContent = msg;
    if (f.parentNode) { f.parentNode.replaceChild(note, f); note.focus && note.setAttribute('tabindex', '-1'); note.focus && note.focus(); }
  }
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || f.dataset.kbAjaxDone) return;
    if (!f.action || !/\/contact(\b|[/?#])/.test(f.action)) return;
    // Only genuine Shopify contact forms ({% form 'contact' %} renders form_type=contact).
    // NEVER the footer newsletter / customer form ({% form 'customer' %} -> form_type=customer,
    // also posts to /contact) — hijacking it would show bridal copy + inflate generate_lead.
    var ft = f.querySelector('[name="form_type"]');
    if (!ft || ft.value !== 'contact') return;
    if (!window.fetch || !window.FormData) return; // native fallback
    if (f.querySelector('input[type="file"]')) return; // uploads go by the native multipart submit, never a background copy
    var honeypot = f.querySelector('[name="contact[website]"], [name="website"]');
    if (honeypot && honeypot.value) return; // let native handle likely-spam
    e.preventDefault();
    var btn = f.querySelector('[type="submit"]');
    var orig = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); btn.innerHTML = '<span>' + (f.getAttribute('data-kb-sending') || 'Sending…') + '</span>'; }
    fetch(f.action, { method: 'POST', body: new FormData(f), headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        // A Shopify contact success is a 302 that fetch follows to a 200 (res.ok true).
        // A 422 (invalid/blank email, spam throttle) re-renders the form with errors —
        // do NOT fake success (that would wipe Saved Looks + inflate the lead + lose the
        // enquiry). Fall back to the native submit so the bride sees the error and can fix it.
        // Shopify can also answer with its bot-challenge page: a redirect to /challenge that
        // fetch follows to a 200. That is NOT a delivered enquiry. Hand over to the native
        // submit so the visitor sees the challenge and Shopify sends the form once it is passed.
        var challenged = !!(res && res.url && /^https?:\/\/[^/]+\/challenge(?:[/?#]|$)/.test(res.url));
        if (!res || !res.ok || challenged) {
          f.dataset.kbAjaxDone = '1';
          if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); btn.innerHTML = orig; }
          f.submit();
          return;
        }
        kbShowSent(f);
        try { push('generate_lead', { kb_lead_type: f.getAttribute('data-kb-form-type') || 'lead' }); } catch (e2) {}
        try { document.dispatchEvent(new CustomEvent('kb:lead:sent', { detail: { form: f } })); } catch (e3) {}
      })
      .catch(function () {
        f.dataset.kbAjaxDone = '1';
        if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); btn.innerHTML = orig; }
        f.submit();
      });
  }, false);

  // Karigur: mirror every contact-form submission to the team (bridal@ + raza@)
  // via the lead-notify function, independent of Shopify's single-recipient
  // store contact email and any Gmail filter. keepalive so it survives the redirect.
  // The function lives on the shop's own Supabase project (moved 2026-10-12; the old copy on
  // another project had no email key and answered 501). It only accepts karigur.com origins.
  var KB_NOTIFY = 'https://dlrpjifzpaktovihmtpt.supabase.co/functions/v1/kb-lead-notify';
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || !f.action || !/\/contact(\b|[/?#])/.test(f.action)) return;
    var mft = f.querySelector('[name="form_type"]');
    if (!mft || mft.value !== 'contact') return; // never mirror the newsletter/customer form
    try {
      var fields = {}, name = '', email = '';
      var formType = f.getAttribute('data-kb-form-type') || (document.title || 'Website form').split('|')[0].trim();
      Array.prototype.forEach.call(f.elements, function (el) {
        if (!el.name || el.disabled) return;
        if (el.type === 'file' || el.type === 'submit' || el.type === 'button') return; // a file input's value is only a fake path
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return; // an unchecked option was never chosen
        if (el.name === 'form_type' || el.name === 'utf8') return; // Shopify plumbing, not the enquiry
        var val = (el.value || '').trim();
        var m = el.name.match(/^contact\[(.+)\]$/);
        var key = m ? m[1] : el.name;
        if (key === 'website') return; // honeypot
        if (!val) return;
        if (/^name$/i.test(key)) name = val;
        else if (/^email$/i.test(key)) email = val;
        else fields[key] = val;
      });
      fetch(KB_NOTIFY, {
        method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formType: formType, name: name, email: email, fields: fields })
      }).catch(function () {});
    } catch (err) {}
  }, { passive: true });

})();
